import { useEffect, useState } from 'react';
import { usePowerSyncDb } from '../adapters/powersync';
import { useUploadQueueStats } from './useUploadQueueStats';
import {
  deriveSyncHealthState,
  getRecentUploadErrors,
  type SyncHealthState,
  type LocalUploadErrorEntry,
} from './syncHealth';

export interface SyncStatus {
  /** Derived high-level health state. */
  state: SyncHealthState;
  /** Live PowerSync connection flag. */
  connected: boolean;
  connecting: boolean;
  /** Actively downloading or uploading right now. */
  inFlight: boolean;
  /** Whether an initial full sync has completed at least once. */
  hasSynced: boolean;
  /** Last successful sync as a locale string (for display), or null. */
  lastSyncedAt: string | null;
  /** Raw ISO timestamp of last sync (for the bundle), or null. */
  lastSyncedAtIso: string | null;
  /** Pending upload op count. */
  pendingCount: number;
  /** Estimated serialized size of the pending queue, in bytes. */
  estimatedSizeBytes: number;
  /** Most recent captured upload error message (summary only), or null. */
  lastErrorMessage: string | null;
  /** Recent captured upload errors (newest first). */
  recentErrors: LocalUploadErrorEntry[];
  /** Browser network reachability. */
  online: boolean;
}

const POLL_INTERVAL_MS = 2_000;

/**
 * PRD.253 §7.1 — `useSyncStatus()`.
 *
 * Combines PowerSync's live status, the upload queue, captured local errors,
 * and the browser online flag into a single derived health state.
 */
export function useSyncStatus(): SyncStatus {
  const { db } = usePowerSyncDb();
  const queue = useUploadQueueStats();

  const [snapshot, setSnapshot] = useState<{
    connected: boolean;
    connecting: boolean;
    inFlight: boolean;
    hasSynced: boolean;
    lastSyncedAt: string | null;
    lastSyncedAtIso: string | null;
    syncErrorMessage: string | null;
  }>({
    connected: false,
    connecting: false,
    inFlight: false,
    hasSynced: false,
    lastSyncedAt: null,
    lastSyncedAtIso: null,
    syncErrorMessage: null,
  });

  const [online, setOnline] = useState<boolean>(
    typeof navigator !== 'undefined' ? navigator.onLine : true,
  );
  const [recentErrors, setRecentErrors] = useState<LocalUploadErrorEntry[]>(() =>
    getRecentUploadErrors(),
  );

  // Track browser online/offline.
  useEffect(() => {
    function update() {
      setOnline(navigator.onLine);
    }
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, []);

  // Poll PowerSync status + re-read captured errors.
  useEffect(() => {
    if (!db) return;

    function update() {
      const s = db!.currentStatus;
      const lastSyncedDate = s?.lastSyncedAt ? new Date(s.lastSyncedAt) : null;
      const downloadErr = s?.dataFlowStatus?.downloadError?.message ?? null;
      const uploadErr = s?.dataFlowStatus?.uploadError?.message ?? null;
      setSnapshot({
        connected: s?.connected ?? false,
        connecting: s?.connecting ?? false,
        inFlight: (s?.dataFlowStatus?.downloading ?? false) || (s?.dataFlowStatus?.uploading ?? false),
        hasSynced: s?.hasSynced ?? false,
        lastSyncedAt: lastSyncedDate ? lastSyncedDate.toLocaleString() : null,
        lastSyncedAtIso: lastSyncedDate ? lastSyncedDate.toISOString() : null,
        // Active sync error reported by PowerSync (download/websocket or upload).
        syncErrorMessage: downloadErr || uploadErr || null,
      });
      setRecentErrors(getRecentUploadErrors());
    }

    update();
    const interval = setInterval(update, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [db]);

  // Prefer a captured upload error; fall back to the live PowerSync sync error
  // (e.g. a websocket/download failure that never produced an upload error).
  const lastErrorMessage = recentErrors[0]?.error?.message ?? snapshot.syncErrorMessage ?? null;

  const state = deriveSyncHealthState({
    connected: snapshot.connected,
    online,
    inFlight: snapshot.inFlight,
    pendingCount: queue.count,
    oldestPendingAt: queue.oldestPendingAt,
    lastSyncedAt: snapshot.lastSyncedAtIso,
    recentErrorCount: recentErrors.length,
    hasSyncError: Boolean(snapshot.syncErrorMessage),
  });

  return {
    state,
    connected: snapshot.connected,
    connecting: snapshot.connecting,
    inFlight: snapshot.inFlight,
    hasSynced: snapshot.hasSynced,
    lastSyncedAt: snapshot.lastSyncedAt,
    lastSyncedAtIso: snapshot.lastSyncedAtIso,
    pendingCount: queue.count,
    estimatedSizeBytes: queue.estimatedSizeBytes,
    lastErrorMessage,
    recentErrors,
    online,
  };
}
